import React, { useState, useEffect } from 'react';
import { getTickets, updateTicket } from '@/lib/api';
import { MessageSquare, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const statusColors = {
  open: 'bg-[#F5E6E6] text-[#8A3D3D]',
  in_progress: 'bg-[#F4EFE6] text-[#7A6849]',
  resolved: 'bg-[#E8EDE5] text-[#3A4035]',
  closed: 'bg-[#F2F0EB] text-[#5C5C55]',
};

const priorityColors = {
  high: 'bg-[#F5E6E6] text-[#8A3D3D]',
  medium: 'bg-[#F4EFE6] text-[#7A6849]',
  low: 'bg-[#F2F0EB] text-[#5C5C55]',
};

export default function TicketsPage() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    getTickets().then(setTickets).catch(console.error).finally(() => setLoading(false));
  }, []);

  const handleStatusUpdate = async (id, status) => {
    try {
      const updated = await updateTicket(id, { status });
      setTickets(prev => prev.map(t => t.ticket_id === id ? updated : t));
    } catch (err) { console.error(err); }
  };

  const filtered = filter === 'all' ? tickets : tickets.filter(t => t.status === filter);

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-[#3A4035] animate-spin" /></div>;

  return (
    <div className="animate-fade-in" data-testid="tickets-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-medium text-[#1A1A1A] tracking-tight">Support Tickets</h1>
          <p className="text-sm text-[#5C5C55] mt-1">{tickets.length} total tickets</p>
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-full sm:w-44 bg-white rounded-xl border-[#E5E2DC] h-10" data-testid="ticket-filter">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="resolved">Resolved</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-3">
        {filtered.map((tkt, i) => (
          <div key={tkt.ticket_id}
            className={`bg-white rounded-2xl border border-[#E5E2DC] shadow-[0_2px_10px_rgba(0,0,0,0.02)] overflow-hidden transition-all duration-300 animate-fade-in stagger-${(i % 6) + 1}`}
            data-testid={`ticket-card-${tkt.ticket_id}`}>
            <button className="w-full p-5 flex items-center gap-4 text-left" onClick={() => setExpandedId(expandedId === tkt.ticket_id ? null : tkt.ticket_id)}>
              <div className="w-10 h-10 rounded-xl bg-[#F2F0EB] flex items-center justify-center shrink-0">
                <MessageSquare className="w-5 h-5 text-[#5C5C55]" strokeWidth={1.5} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#1A1A1A] truncate">{tkt.subject}</p>
                <p className="text-xs text-[#8A8B85]">{tkt.reporter_name} — {tkt.category}</p>
              </div>
              <span className={`text-xs px-2.5 py-1 rounded-full font-medium capitalize shrink-0 ${priorityColors[tkt.priority] || ''}`}>{tkt.priority}</span>
              <span className={`text-xs px-2.5 py-1 rounded-full font-medium capitalize shrink-0 ${statusColors[tkt.status] || ''}`}>{tkt.status?.replace('_', ' ')}</span>
              {expandedId === tkt.ticket_id ? <ChevronUp className="w-4 h-4 text-[#8A8B85] shrink-0" strokeWidth={1.5} /> : <ChevronDown className="w-4 h-4 text-[#8A8B85] shrink-0" strokeWidth={1.5} />}
            </button>
            {expandedId === tkt.ticket_id && (
              <div className="px-5 pb-5 border-t border-[#F2F0EB] pt-4 space-y-3">
                <p className="text-sm text-[#5C5C55]">{tkt.description}</p>
                {tkt.notes && <p className="text-sm text-[#5C5C55] bg-[#F2F0EB] rounded-xl p-3"><strong>Notes:</strong> {tkt.notes}</p>}
                {tkt.assigned_to && <p className="text-xs text-[#8A8B85]">Assigned to: {tkt.assigned_to}</p>}
                <div className="flex gap-2 pt-2">
                  {['open', 'in_progress', 'resolved', 'closed'].map(st => (
                    <button key={st} onClick={() => handleStatusUpdate(tkt.ticket_id, st)}
                      className={`text-xs px-3 py-1.5 rounded-full font-medium capitalize transition-colors ${tkt.status === st ? 'bg-[#3A4035] text-white' : 'bg-[#F2F0EB] text-[#5C5C55] hover:bg-[#E5E7E1]'}`}
                      data-testid={`ticket-status-${st}`}>
                      {st.replace('_', ' ')}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      {filtered.length === 0 && (
        <div className="text-center py-16">
          <MessageSquare className="w-10 h-10 text-[#D8DBD3] mx-auto mb-3" strokeWidth={1.5} />
          <p className="text-sm text-[#8A8B85]">No tickets found</p>
        </div>
      )}
    </div>
  );
}
